/**
 * [DUBAI 2026-10-19, user] The GITEX GLOBAL Dubai contest board — what /api/dubai-contest/board sends, and the figures
 * every page derives from it. Same derivations as the approved preview (.claude/tools/dubai-contest/dubai-contest-looks.html):
 * people ranked by score, then sales, then name (the agreed tie rule); the chase; one plain sentence per page.
 * Every rule that decides a NUMBER lives on the server (services/dubaiContest.ts) — nothing here re-scores anything.
 */
export type Cat = 'c' | 'p' | 'f' | 'x';
export type MixKey = Cat | 's';

export interface DubaiPerson {
  n: string; t: string; s: number; sales: number; owned: number; assists: number;
  m: Record<MixKey, number>; refunds: number;
}
export interface DubaiDeal {
  id: string; when: string; owner: string; site: string; amount: number; src: string; k: Cat; w: number; pts: number;
  ownerPts: number; voices: { n: string; pts: number; out: boolean }[]; repeat: boolean; why: string; refunded: number;
}
export interface DubaiPayload {
  computedAt: string; readAt: string; people: DubaiPerson[]; deals: DubaiDeal[]; leftOut: number; oneTime: number;
  feed: { id: string; at: string }[]; stale: boolean; stamp: string;
}

export interface Ranked extends DubaiPerson { rank: number }
export interface ChaseRow { p: Ranked; above: Ranked; gapNext: number; gap1: number; needCompany: number; needPast: number }
export interface Model {
  note: string;
  people: Ranked[];
  scored: Ranked[];
  yet: Ranked[];
  lead: Ranked | null;
  second: Ranked | null;
  byName: Map<string, Ranked>;
  total: number;
  deals: DubaiDeal[];          // newest first
  assisted: DubaiDeal[];
  helpers: Ranked[];
  latest: DubaiDeal | null;
  chase: ChaseRow[];
  take: { standings: string; scorecard: string; chase: string; voice: string };
}

export const r2 = (n: number): number => Math.round(n * 100) / 100;
export const money = (n: number, cents = true): string =>
  '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 });
export const esc = (s: unknown): string =>
  String(s).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' } as Record<string, string>)[ch]);
export const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;
const pctOf = (a: number, b: number): number => (b ? Math.round((a / b) * 1000) / 10 : 0);

export const CATS: [MixKey, string, string][] = [
  ['c', 'Company lead', '100%'], ['p', 'Past client', '80%'], ['f', 'Conference / referral', '50%'],
  ['x', 'No source found', '100%'], ['s', 'Second voice', '30%'],
];
const CAT_PLURAL: Record<MixKey, string> = {
  c: 'Company leads', p: 'Past clients', f: 'Conference and referral deals', x: 'Deals with no source', s: 'Second-voice shares',
};
export const RULES = 'Company lead 100% · Past client 80% · Conference / referral 50% · Second voice: owner 70%, helper 30%';

/** Contest close: 1 Dec 2026 12:30 PM IST = 07:00 UTC (the last moment that counts is 12:29 PM IST). */
export const CONTEST_END_MS = Date.UTC(2026, 11, 1, 7, 0, 0);
export const daysLeft = (): number => Math.max(0, Math.floor((CONTEST_END_MS - Date.now()) / 86400000));
export const dubaiTime = (): string => {
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date());
  } catch {
    return '';
  }
};

export function buildModel(p: DubaiPayload): Model {
  const people: Ranked[] = p.people.slice()
    .sort((a, b) => b.s - a.s || b.sales - a.sales || a.n.localeCompare(b.n))
    .map((x, i) => ({ ...x, rank: i + 1 }));
  const scored = people.filter((x) => x.s > 0);
  const yet = people.filter((x) => x.s <= 0);
  const lead = scored[0] || null;
  const second = scored[1] || null;
  const total = r2(scored.reduce((a, x) => a + x.s, 0));
  const bySrc = CATS.map(([k]) => [k, r2(scored.reduce((a, x) => a + (x.m[k] || 0), 0))] as [MixKey, number]);
  const topSrc = bySrc.slice().sort((a, b) => b[1] - a[1])[0];
  const earned = r2(bySrc.reduce((a, [, v]) => a + v, 0));
  const assisted = p.deals.filter((d) => d.voices.length);
  const helpers = people.filter((x) => (x.m.s || 0) > 0).sort((a, b) => b.m.s - a.m.s || b.assists - a.assists || a.n.localeCompare(b.n));
  // The chase: everyone behind #1, what it takes to pass the person directly above, and the gap to #1.
  const chase: ChaseRow[] = lead ? scored.slice(1).map((x) => {
    const above = scored[x.rank - 2];
    const gapNext = r2(above.s - x.s);
    return { p: x, above, gapNext, gap1: r2(lead.s - x.s), needCompany: gapNext, needPast: r2(gapNext / 0.8) };
  }) : [];
  const take = {
    standings: lead && second ? `${lead.n} leads by ${money(lead.s - second.s)} over ${second.n}`
      : lead ? `${lead.n} leads with ${money(lead.s)}` : 'The first deal takes the lead',
    // [DUBAI-REFUND-COL] the categories are as earned (before refunds), so the share is of the earned total
    scorecard: earned && topSrc ? `${CAT_PLURAL[topSrc[0]]} bring ${pctOf(topSrc[1], earned)}% of all points` : 'Every point, and where it came from',
    chase: lead && second ? `${second.n} is ${money(lead.s - second.s)} behind ${lead.n}` : 'Everyone is chasing the first deal',
    voice: helpers[0] ? `${helpers[0].n} leads the second voices with ${money(helpers[0].m.s)} from ${plural(helpers[0].assists, 'deal', 'deals')}`
      : 'No second voice yet',
  };
  return {
    note: `Live data, read ${p.readAt}`,
    people, scored, yet, lead, second, byName: new Map(people.map((x) => [x.n, x])), total,
    deals: p.deals, assisted, helpers, latest: p.deals[0] || null, chase, take,
  };
}
